import { notification } from "antd";

import { IQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/Domain/types";
import useHeaderOperations from "@Totum/QuestionnaireBuilder/Application/hooks/useHeaderOperations";
import useCurrentQuestionnaireId from "@Totum/QuestionnaireBuilder/State/hooks/useCurrentQuestionnaireId";
import { isPresent } from "@Totum/Main/Utils";
import { DefaultKeys } from "@Totum/QuestionnaireBuilder/Constants";
import { useCurrentQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/State/hooks/useCurrentQuestionnaireDetails";

/**
 * Presents the `Header` component with the UI model and handlers.
 */
const useHeaderPresenter = () => {
    // ** State ** //
    const [currentQuestionnaireId, setCurrentQuestionnaireId] = useCurrentQuestionnaireId();
    const [currentQuestionnaireDetails] = useCurrentQuestionnaireDetails();

    // ** Hooks ** //
    const {
        deleteQuestionnaire,
        publishQuestionnaire,

        isPublishing,
        isFetchingQuestionnaires,
        questionnaires,
    } = useHeaderOperations();

    // ** Constants & Variables ** //

    const currentQuestionnaire = questionnaires?.find((q) => q.questionnaireId === currentQuestionnaireId);
    const hasQuestionnaire = isPresent(currentQuestionnaire);
    const hasQuestionnaires = isPresent(questionnaires);

    const hasQuestions = Boolean(currentQuestionnaireDetails?.questions.length);
    const isPublished = Boolean(currentQuestionnaire?.isPublished);

    // ** Handlers ** //

    /**
     * Handles the delete action for the questionnaire
     */
    const handleDelete = async (): Promise<void> => {
        const questionnaireId = currentQuestionnaire?.questionnaireId;

        if (!questionnaireId) {
            return Promise.reject("Questionnaire ID is missing");
        }
        const operationResult = await deleteQuestionnaire(questionnaireId);

        if (!operationResult.hasAction) {
            return;
        }

        if (operationResult.isSuccess) {
            notification.success({
                message: "This questionnaire has been deleted.",
            });
            setCurrentQuestionnaireId(undefined);
        } else {
            notification.error({
                message: "Failed to delete this questionnaire.",
            });
        }
    };

    /**
     * Handles the duplicate action for the questionnaire
     */
    const handlePublishClick = async (): Promise<void> => {
        const questionnaireId = currentQuestionnaire?.questionnaireId;
        if (!questionnaireId) {
            return Promise.reject("Questionnaire ID is missing");
        }
        const operationResult = await publishQuestionnaire(questionnaireId);

        if (!operationResult.hasAction) {
            return;
        }

        if (operationResult.isSuccess) {
            notification.success({
                message: "This questionnaire has been published.",
            });
        } else {
            notification.error({
                message: "Failed to publish this questionnaire.",
            });
        }
    };

    /**
     * Handles the new action for the questionnaire
     * Creates a new questionnaire
     */
    const handleNewClick = (): void => {
        setCurrentQuestionnaireId(DefaultKeys.NEW_QUESTIONNAIRE_ID);
    };

    /**
     * Handles the clear action for the questionnaire
     */
    const handleQuestionnaireClear = (): void => {
        setCurrentQuestionnaireId(undefined);
    };

    /**
     * Handles the selection of a questionnaire
     * @param id The questionnaire id
     */
    const handleQuestionnaireSelect = (id: IQuestionnaireDetails["questionnaireId"]): void => {
        if (!!id) {
            setCurrentQuestionnaireId(id);
        }
    };

    // ** Exports ** //

    return {
        handlers: {
            handleDelete,
            handleNewClick,
            handlePublishClick,
            handleQuestionnaireClear,
            handleQuestionnaireSelect,
        },

        uiModel: {
            optionsForSelect: questionnaires?.filter((q) => q), // remove blank questionnaires from dropdown
            questionnaire: {
                questionnaireId: currentQuestionnaire?.questionnaireId,

                title: currentQuestionnaire?.title,

                // questionnaire flags
                hasQuestions,
                isExistingQuestionnaire: Boolean(currentQuestionnaire?.questionnaireId),
                isLocked: Boolean(currentQuestionnaire?.isLocked),
                isPublished,
            },
            selectFieldPlaceholder: isFetchingQuestionnaires ? "Loading Questionnaires..." : "Select a Questionnaire",
            tooltipMessage: isPublished
                ? "This questionnaire is already published and visible to users."
                : hasQuestions
                ? "Publish the questionnaire to make it available to users."
                : "You must add questions to the questionnaire before you can publish it.",

            // flags
            canShowSelect: hasQuestionnaires || isFetchingQuestionnaires,
            canPublish: hasQuestions && !isPublished,
            isFetchingQuestionnaires,
            isPublishing,
        },
    };
};

export default useHeaderPresenter;
